import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, User as UserIcon, UserCheck, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Group the 6 cross-product flags into 3 visual sections so the UX
// reads as "for tickets I OWN / for tickets ASSIGNED to me / for UNASSIGNED tickets".
const SECTIONS = [
  {
    id: 'owner',
    title: 'Tickets I own',
    description: 'Updates on tickets where I am the client / requester.',
    icon: UserIcon,
    keys: [
      { key: 'ticket_created_owner', label: 'Email me when a new ticket is opened on my behalf' },
      { key: 'ticket_updated_owner', label: 'Email me when one of my tickets is updated' },
    ],
  },
  {
    id: 'assignee',
    title: 'Tickets assigned to me',
    description: 'Notifications about tickets where I am the technician.',
    icon: UserCheck,
    keys: [
      { key: 'ticket_created_assignee', label: 'Email me when a ticket is freshly assigned to me' },
      { key: 'ticket_updated_assignee', label: 'Email me on activity for tickets assigned to me' },
    ],
  },
  {
    id: 'admin',
    title: 'Unassigned tickets (admin triage)',
    description: 'Notifications about tickets that have no technician yet.',
    icon: Shield,
    keys: [
      { key: 'ticket_created_admin', label: 'Email me when a new unassigned ticket needs triage' },
      { key: 'ticket_updated_admin', label: 'Email me when an unassigned ticket has activity' },
    ],
  },
];

/**
 * Self-service notification preferences modal.
 *
 * Used by staff to opt out of any of the 6 ticket-event email categories. Backed
 * by GET/PUT /api/auth/notification-prefs. Defaults are opt-in (every checkbox
 * starts ON) — toggle off to silence that category.
 */
const NotificationPrefsDialog = ({ open, onClose }) => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/auth/notification-prefs`, { withCredentials: true })
      .then((res) => {
        if (!cancelled) setPrefs(res.data?.prefs || {});
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load notification preferences');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  const togglePref = (key) => {
    setPrefs((prev) => ({ ...(prev || {}), [key]: !(prev?.[key] ?? true) }));
  };

  const handleSave = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/auth/notification-prefs`,
        { prefs },
        { withCredentials: true },
      );
      setPrefs(res.data?.prefs || prefs);
      toast.success('Notification preferences saved');
      onClose?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  // Quick action — turn everything off (handy for vacations / DND).
  const muteAll = () => {
    if (!prefs) return;
    const muted = {};
    Object.keys(prefs).forEach((k) => { muted[k] = false; });
    setPrefs(muted);
  };

  const allMuted = prefs && Object.values(prefs).every((v) => v === false);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="notification-prefs-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black uppercase tracking-tight flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Notification Preferences
          </DialogTitle>
          <DialogDescription>
            Choose which ticket emails you receive. Defaults are on; switch off to silence a category.
          </DialogDescription>
        </DialogHeader>

        {loading || !prefs ? (
          <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
        ) : (
          <div className="space-y-5 py-2">
            {SECTIONS.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.id}
                  className="space-y-3 p-4 rounded-lg border border-border/60 bg-muted/10"
                  data-testid={`prefs-section-${section.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold uppercase text-xs tracking-widest text-foreground">{section.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{section.description}</p>
                    </div>
                  </div>
                  <div className="space-y-2 pl-12">
                    {section.keys.map(({ key, label }) => (
                      <div key={key} className="flex items-start justify-between gap-3">
                        <label htmlFor={`pref-${key}`} className="text-sm cursor-pointer flex-1">
                          {label}
                        </label>
                        <Switch
                          id={`pref-${key}`}
                          data-testid={`pref-switch-${key}`}
                          checked={prefs[key] ?? true}
                          onCheckedChange={() => togglePref(key)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="flex items-center justify-between pt-3 border-t border-border/60">
              <Button
                variant="ghost"
                size="sm"
                onClick={muteAll}
                disabled={saving || allMuted}
                data-testid="mute-all-btn"
                className="text-muted-foreground"
              >
                <BellOff className="w-4 h-4 mr-2" />
                Mute everything
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} data-testid="save-prefs-btn" className="font-bold uppercase">
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotificationPrefsDialog;
